// Activo Fijo → Movimientos → Bajas / Retiros.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { getErrorMessage } from '@/lib/api/client';
import { ActivoRow, BajaRow, Campo, MOTIVOS_BAJA, Modal, afRpc, fieldCls, money } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';

const hoy = () => fechaLocal();

export function BajasAF() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching } = useQuery<BajaRow[]>({ queryKey: ['af-bajas'], queryFn: () => afRpc<BajaRow[]>('af_bajas') });
  const { data: activos = [] } = useQuery<ActivoRow[]>({ queryKey: ['af-activos', 'activo', ''], queryFn: () => afRpc<ActivoRow[]>('af_activos', { _estado: 'activo', _busq: null }) });
  const [abierto, setAbierto] = useState(false);
  const [form, setForm] = useState({ activo_id: '', fecha: hoy(), motivo: 'obsolescencia', observaciones: '' });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));
  const sel = useMemo(() => activos.find((a) => a.id === form.activo_id), [activos, form.activo_id]);

  const registrar = useMutation({
    mutationFn: () => afRpc<any>('af_baja_registrar', { _activo_id: form.activo_id, _fecha: form.fecha, _motivo: form.motivo, _observaciones: form.observaciones || null }),
    onSuccess: (r) => {
      if (r.contabilizada) toast.success(`Baja registrada · pérdida ${money(r.valor_en_libros)} · asiento generado`);
      else toast.warning(`Baja registrada (pérdida ${money(r.valor_en_libros)}) SIN asiento: ${r.aviso ?? 'revisa Mapeo contable'}`);
      setAbierto(false); void qc.invalidateQueries({ queryKey: ['af-bajas'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const anular = useMutation({
    mutationFn: (id: string) => afRpc('af_baja_anular', { _baja_id: id }),
    onSuccess: () => { toast.success('Baja anulada · activo restaurado'); void qc.invalidateQueries({ queryKey: ['af-bajas'] }); void qc.invalidateQueries({ queryKey: ['af-activos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Trash2 className="w-5 h-5" /> Bajas / Retiros</h1>
        <Button onClick={() => { setForm({ activo_id: '', fecha: hoy(), motivo: 'obsolescencia', observaciones: '' }); setAbierto(true); }} disabled={activos.length === 0}><Plus className="w-4 h-4 mr-1" /> Registrar baja</Button>
      </div>
      <p className="text-sm text-muted-foreground">Retiro por obsolescencia, robo, siniestro o donación. Se retira el costo y la depreciación acumulada y se reconoce la pérdida por el valor en libros. Corre la depreciación del mes antes de dar de baja.</p>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Bajas ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Placa</th><th className="py-2 pr-3">Activo</th><th className="py-2 pr-3">Motivo</th><th className="py-2 pr-3 text-right">Pérdida (valor en libros)</th><th className="py-2 pr-3">Asiento</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin bajas</td></tr>}
              {rows.map((b) => (
                <tr key={b.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{b.fecha}</td>
                  <td className="py-2 pr-3 font-mono">{b.placa}</td>
                  <td className="py-2 pr-3">{b.activo}</td>
                  <td className="py-2 pr-3">{MOTIVOS_BAJA.find((m) => m.v === b.motivo)?.l ?? b.motivo}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular-nums">{money(b.valor_en_libros)}</td>
                  <td className="py-2 pr-3">{b.asiento_numero ? `#${b.asiento_numero}` : '—'}</td>
                  <td className="py-2 pr-3">{b.estado === 'anulada' ? <span className="text-xs text-muted-foreground">anulada</span> : <span className="rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-xs">registrada</span>}</td>
                  <td className="py-2 text-right">{b.estado === 'registrada' && <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Anular" onClick={async () => { if (await dlg.confirmar(`¿Anular la baja de ${b.placa}?`, { detalle: 'El activo volverá a estar activo.', peligro: true })) anular.mutate(b.id); }}><Ban className="w-4 h-4" /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title="Registrar baja" onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <Campo label="Activo *">
              <select className={fieldCls} value={form.activo_id} onChange={(e) => set({ activo_id: e.target.value })}>
                <option value="">— Selecciona —</option>
                {activos.map((a) => <option key={a.id} value={a.id}>{a.placa} · {a.nombre} (valor en libros {money(a.valor_libros)})</option>)}
              </select>
            </Campo>
            {sel && <p className="text-sm rounded-md border p-2 bg-muted/20">Costo {money(sel.costo)} · Valor en libros <b>{money(sel.valor_libros)}</b> → esta será la pérdida reconocida.</p>}
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Fecha *"><input type="date" className={fieldCls} value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} /></Campo>
              <Campo label="Motivo *"><select className={fieldCls} value={form.motivo} onChange={(e) => set({ motivo: e.target.value })}>{MOTIVOS_BAJA.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}</select></Campo>
            </div>
            <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={form.observaciones} onChange={(e) => set({ observaciones: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 border-t pt-3">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={registrar.isPending} disabled={!form.activo_id} onClick={() => registrar.mutate()}>Registrar baja</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
